import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Phone, Mail, MessageSquare, Edit, Trash2, Upload, RefreshCw, BarChart3 } from 'lucide-react';
import { toast } from 'sonner';
import CallAnalysisModal from '@/components/CallAnalysisModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Leads = () => {
  const { getAuthHeader } = useAuth();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [autoSyncEnabled, setAutoSyncEnabled] = useState(false);
  const [selectedLeads, setSelectedLeads] = useState(new Set());
  const [syncing, setSyncing] = useState(false);
  const [analysisModalOpen, setAnalysisModalOpen] = useState(false);
  const [selectedLeadForAnalysis, setSelectedLeadForAnalysis] = useState(null);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    email: '',
    source: 'manual',
    notes: ''
  });

  useEffect(() => {
    fetchLeads();
  }, []);

  const fetchLeads = async () => {
    try {
      const response = await axios.get(`${API}/leads`, {
        headers: getAuthHeader()
      });
      setLeads(response.data);
    } catch (error) {
      toast.error('Failed to fetch leads');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateLead = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/leads`, formData, {
        headers: getAuthHeader()
      });
      toast.success('Lead created successfully');
      setDialogOpen(false);
      setFormData({
        first_name: '',
        last_name: '',
        phone: '',
        email: '',
        source: 'manual',
        notes: ''
      });
      fetchLeads();
    } catch (error) {
      toast.error('Failed to create lead');
    }
  };

  const handleDeleteLead = async (leadId) => {
    if (!window.confirm('Are you sure you want to delete this lead?')) return;
    try {
      await axios.delete(`${API}/leads/${leadId}`, {
        headers: getAuthHeader()
      });
      toast.success('Lead deleted successfully');
      fetchLeads();
    } catch (error) {
      toast.error('Failed to delete lead');
    }
  };

  const handleSelectLead = (leadId) => {
    setSelectedLeads(prev => {
      const newSet = new Set(prev);
      if (newSet.has(leadId)) {
        newSet.delete(leadId);
      } else {
        newSet.add(leadId);
      }
      return newSet;
    });
  };

  const handleSyncSelectedToFUB = async () => {
    if (selectedLeads.size === 0) {
      toast.error('Please select at least one lead to sync');
      return;
    }

    setSyncing(true);
    let successCount = 0;
    let errorCount = 0;

    for (const leadId of selectedLeads) {
      const lead = leads.find(l => l.id === leadId);
      if (!lead) continue;

      try {
        await axios.post(`${API}/fub/create-lead`, {
          person_data: {
            firstName: lead.first_name,
            lastName: lead.last_name || '',
            email: lead.email || '',
            phone: lead.phone || ''
          },
          source: 'PropFlo Dashboard',
          message: `Lead Type: ${lead.lead_type.toUpperCase()}\nNotes: ${lead.notes || 'No notes'}`
        });
        successCount++;
      } catch (error) {
        console.error(`Failed to sync lead ${leadId}:`, error);
        errorCount++;
      }
    }

    setSyncing(false);
    setSelectedLeads(new Set()); // Clear selection

    if (successCount > 0) {
      toast.success(`Successfully synced ${successCount} lead${successCount > 1 ? 's' : ''} to FUB`);
    }
    if (errorCount > 0) {
      toast.error(`Failed to sync ${errorCount} lead${errorCount > 1 ? 's' : ''}`);
    }
  };

  const getLeadTypeColor = (type) => {
    switch (type) {
      case 'hot':
        return 'bg-red-100 text-red-700';
      case 'warm':
        return 'bg-orange-100 text-orange-700';
      case 'cold':
        return 'bg-cyan-100 text-cyan-700';
      default:
        return 'bg-slate-100 text-slate-700';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-slate-600 dark:text-slate-400">Loading leads...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-slate-800 dark:text-white mb-2">Leads</h1>
          <p className="text-slate-600 dark:text-slate-400">Manage and qualify your real estate prospects</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button
              data-testid="create-lead-button"
              className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Lead
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
            <DialogHeader>
              <DialogTitle className="text-slate-800 dark:text-white">Create New Lead</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateLead} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2 text-slate-700 dark:text-slate-300">First Name *</label>
                <Input
                  data-testid="lead-firstname-input"
                  value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  className="bg-white dark:bg-slate-700 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-white"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2 text-slate-700 dark:text-slate-300">Last Name</label>
                <Input
                  data-testid="lead-lastname-input"
                  value={formData.last_name}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  className="bg-white dark:bg-slate-700 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2 text-slate-700 dark:text-slate-300">Phone *</label>
                <Input
                  data-testid="lead-phone-input"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="bg-white dark:bg-slate-700 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-white"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2 text-slate-700 dark:text-slate-300">Email</label>
                <Input
                  data-testid="lead-email-input"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="bg-white dark:bg-slate-700 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2 text-slate-700 dark:text-slate-300">Notes</label>
                <Input
                  data-testid="lead-notes-input"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="bg-white dark:bg-slate-700 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-white"
                />
              </div>
              <Button
                data-testid="submit-lead-button"
                type="submit"
                className="w-full bg-gradient-to-r from-blue-500 to-purple-600"
              >
                Create Lead
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* FUB Sync & Bulk Actions Card */}
      <Card className="p-6 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-slate-800 dark:to-slate-700 border-blue-200 dark:border-slate-600">
        <div className="flex items-center justify-between">
          <div className="space-y-2">
            <div className="flex items-center gap-4">
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={autoSyncEnabled}
                  onChange={(e) => setAutoSyncEnabled(e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-slate-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-slate-600 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-slate-600 peer-checked:bg-blue-600"></div>
              </label>
              <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                Auto-Sync to FUB
              </span>
              
              {/* Select All Checkbox */}
              {leads.length > 0 && (
                <>
                  <span className="text-slate-400 dark:text-slate-600">|</span>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedLeads.size === leads.length && leads.length > 0}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedLeads(new Set(leads.map(l => l.id)));
                        } else {
                          setSelectedLeads(new Set());
                        }
                      }}
                      className="w-5 h-5 text-blue-600 bg-slate-100 dark:bg-slate-700 border-slate-300 dark:border-slate-600 rounded focus:ring-blue-500 dark:focus:ring-blue-600 focus:ring-2 cursor-pointer"
                    />
                    <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                      Select All
                    </span>
                  </label>
                </>
              )}
            </div>
            {selectedLeads.size > 0 && (
              <span className="text-sm text-slate-600 dark:text-slate-400">
                {selectedLeads.size} lead{selectedLeads.size > 1 ? 's' : ''} selected
              </span>
            )}
          </div>
          <div className="flex gap-2">
            {selectedLeads.size > 0 && (
              <Button
                onClick={async () => {
                  if (!window.confirm(`Are you sure you want to delete ${selectedLeads.size} selected lead${selectedLeads.size > 1 ? 's' : ''}?`)) return;
                  
                  let successCount = 0;
                  let errorCount = 0;
                  
                  for (const leadId of selectedLeads) {
                    try {
                      await axios.delete(`${API}/leads/${leadId}`, {
                        headers: getAuthHeader()
                      });
                      successCount++;
                    } catch (error) {
                      errorCount++;
                    }
                  }
                  
                  setSelectedLeads(new Set());
                  fetchLeads();
                  
                  if (successCount > 0) {
                    toast.success(`Successfully deleted ${successCount} lead${successCount > 1 ? 's' : ''}`);
                  }
                  if (errorCount > 0) {
                    toast.error(`Failed to delete ${errorCount} lead${errorCount > 1 ? 's' : ''}`);
                  }
                }}
                className="bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete Selected
              </Button>
            )}
            <Button
              onClick={handleSyncSelectedToFUB}
              disabled={selectedLeads.size === 0 || syncing}
              className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 disabled:opacity-50"
            >
              {syncing ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Syncing...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Sync Selected to FUB
                </>
              )}
            </Button>
          </div>
        </div>
      </Card>

      {/* Leads Grid */}
      {leads.length === 0 ? (
        <Card className="p-12 text-center bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
          <p className="text-slate-600 dark:text-slate-400 mb-4">No leads yet. Create your first lead to get started!</p>
          <Button
            onClick={() => setDialogOpen(true)}
            className="bg-gradient-to-r from-blue-500 to-purple-600"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Your First Lead
          </Button>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {leads.map((lead) => (
            <Card
              key={lead.id}
              data-testid={`lead-card-${lead.id}`}
              className="p-6 card-hover bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 shadow-lg relative"
            >
              {/* Checkbox for selection */}
              <div className="absolute top-4 left-4">
                <input
                  type="checkbox"
                  checked={selectedLeads.has(lead.id)}
                  onChange={() => handleSelectLead(lead.id)}
                  className="w-5 h-5 text-blue-600 bg-slate-100 dark:bg-slate-700 border-slate-300 dark:border-slate-600 rounded focus:ring-blue-500 dark:focus:ring-blue-600 focus:ring-2 cursor-pointer"
                />
              </div>
              
              <div className="flex items-start justify-between mb-4 ml-8">
                <div>
                  <h3 className="text-lg font-bold text-slate-800 dark:text-white">
                    {lead.first_name} {lead.last_name}
                  </h3>
                  <span
                    className={`inline-block px-2 py-1 rounded-full text-xs font-medium mt-2 ${getLeadTypeColor(
                      lead.lead_type
                    )}`}
                  >
                    {lead.lead_type.toUpperCase()}
                  </span>
                </div>
              </div>

              <div className="space-y-2 mb-4 ml-8">
                <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                  <Phone className="w-4 h-4" />
                  {lead.phone}
                </div>
                {lead.email && (
                  <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                    <Mail className="w-4 h-4" />
                    {lead.email}
                  </div>
                )}
              </div>

              {/* Call Analysis Button - Show if lead has been called */}
              {lead.last_call_id && (
                <div className="mb-3 ml-8">
                  <Button
                    data-testid={`view-analysis-${lead.id}`}
                    size="sm"
                    className="w-full bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700 text-white"
                    onClick={() => {
                      setSelectedLeadForAnalysis(lead.id);
                      setAnalysisModalOpen(true);
                    }}
                    title="View AI-powered call analysis"
                  >
                    <BarChart3 className="w-4 h-4 mr-2" />
                    View Call Analysis
                  </Button>
                </div>
              )}

              <div className="flex gap-2 ml-8">
                <Button
                  data-testid={`call-lead-${lead.id}`}
                  size="sm"
                  variant="outline"
                  className="flex-1 border-slate-300 dark:border-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700"
                  onClick={() => window.open(`tel:${lead.phone}`, '_self')}
                  title="Call lead"
                >
                  <Phone className="w-4 h-4" />
                </Button>
                <Button
                  data-testid={`sms-lead-${lead.id}`}
                  size="sm"
                  variant="outline"
                  className="flex-1 border-slate-300 dark:border-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700"
                  onClick={() => window.open(`sms:${lead.phone}`, '_self')}
                  title="Send SMS"
                >
                  <MessageSquare className="w-4 h-4" />
                </Button>
                <Button
                  data-testid={`email-lead-${lead.id}`}
                  size="sm"
                  variant="outline"
                  className="flex-1 border-slate-300 dark:border-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700"
                  onClick={() => window.open(`mailto:${lead.email}`, '_self')}
                  title="Send email"
                >
                  <Mail className="w-4 h-4" />
                </Button>
                <Button
                  data-testid={`delete-lead-${lead.id}`}
                  size="sm"
                  variant="outline"
                  className="flex-1 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 border-slate-300 dark:border-slate-600"
                  onClick={() => handleDeleteLead(lead.id)}
                  title="Delete lead"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Call Analysis Modal */}
      <CallAnalysisModal
        leadId={selectedLeadForAnalysis}
        isOpen={analysisModalOpen}
        onClose={() => {
          setAnalysisModalOpen(false);
          setSelectedLeadForAnalysis(null);
        }}
      />
    </div>
  );
};

export default Leads;